import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { TURNSTILE_SITE_KEY, CAPTCHA_ERROR_MESSAGE, isCaptchaEnabled, loadTurnstile } from "Helpers/captcha";

const TOKEN_WAIT_MS = 30000;

// Out of the page flow and zero-sized: no effect on the layout around it.
const HIDDEN_STYLE = { position: "absolute", width: 0, height: 0, overflow: "hidden", pointerEvents: "none" };
// Shown only when Cloudflare needs a click: its own line above the buttons.
const VISIBLE_STYLE = { display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%", marginBottom: 16 };

/**
 * Cloudflare Turnstile widget. Mostly invisible: it only shows a checkbox when Cloudflare is unsure.
 * Until then it takes up no space at all, so the form looks exactly as it did without a captcha.
 * Place it on its own line, outside the row that holds the submit button.
 *
 * Usage:
 *   const captchaRef = useRef(null);
 *   <CaptchaWidget ref={captchaRef} action="login" />
 *   const token = await captchaRef.current.getToken();   // waits for the check if still running
 *   ...send captchaHeaders(token) with the request...
 *   captchaRef.current.reset();                           // tokens are single use: reset after every submit
 *
 * Renders nothing and getToken() resolves to null when REACT_APP_TURNSTILE_SITE_KEY is not set.
 */
const CaptchaWidget = forwardRef(function CaptchaWidget({ action, className }, ref) {
	const containerRef = useRef(null);
	const widgetIdRef = useRef(null);
	const tokenRef = useRef(null);
	const waitersRef = useRef([]);
	const [loadFailed, setLoadFailed] = useState(false);
	// True only while Cloudflare asks the visitor to click the checkbox.
	const [needsInteraction, setNeedsInteraction] = useState(false);

	const settleWaiters = (token) => {
		const waiters = waitersRef.current;
		waitersRef.current = [];
		waiters.forEach((w) => {
			clearTimeout(w.timer);
			if (token) w.resolve(token);
			else w.reject(new Error(CAPTCHA_ERROR_MESSAGE));
		});
	};

	useEffect(() => {
		if (!isCaptchaEnabled()) return undefined;
		let cancelled = false;

		loadTurnstile()
			.then((turnstile) => {
				if (cancelled || !containerRef.current) return;
				widgetIdRef.current = turnstile.render(containerRef.current, {
					sitekey: TURNSTILE_SITE_KEY,
					action,
					appearance: "interaction-only",
					"before-interactive-callback": () => setNeedsInteraction(true),
					"after-interactive-callback": () => setNeedsInteraction(false),
					callback: (token) => {
						tokenRef.current = token;
						setLoadFailed(false);
						settleWaiters(token);
					},
					"expired-callback": () => {
						// Turnstile refreshes the token on its own; drop the stale one meanwhile.
						tokenRef.current = null;
					},
					"error-callback": () => {
						tokenRef.current = null;
						settleWaiters(null);
						// Returning nothing lets Turnstile retry by itself.
					},
				});
			})
			.catch(() => {
				if (cancelled) return;
				setLoadFailed(true);
				settleWaiters(null);
			});

		return () => {
			cancelled = true;
			if (widgetIdRef.current !== null && window.turnstile) {
				try {
					window.turnstile.remove(widgetIdRef.current);
				} catch (e) {
					// widget already gone
				}
			}
			widgetIdRef.current = null;
			tokenRef.current = null;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [action]);

	useImperativeHandle(ref, () => ({
		getToken() {
			if (!isCaptchaEnabled()) return Promise.resolve(null);
			if (tokenRef.current) return Promise.resolve(tokenRef.current);
			return new Promise((resolve, reject) => {
				const waiter = { resolve, reject };
				waiter.timer = setTimeout(() => {
					waitersRef.current = waitersRef.current.filter((w) => w !== waiter);
					reject(new Error(CAPTCHA_ERROR_MESSAGE));
				}, TOKEN_WAIT_MS);
				waitersRef.current.push(waiter);
			});
		},
		reset() {
			tokenRef.current = null;
			if (window.turnstile && widgetIdRef.current !== null) {
				try {
					window.turnstile.reset(widgetIdRef.current);
				} catch (e) {
					// widget already gone
				}
			}
		},
	}));

	if (!isCaptchaEnabled()) return null;

	const isVisible = needsInteraction || loadFailed;

	return (
		<div className={className} style={isVisible ? VISIBLE_STYLE : HIDDEN_STYLE} aria-hidden={!isVisible}>
			<div ref={containerRef} />
			{loadFailed && <p style={{ color: "#d32f2f", fontSize: 13, margin: "6px 0 0" }}>{CAPTCHA_ERROR_MESSAGE}</p>}
		</div>
	);
});

export default CaptchaWidget;
